import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../AuthProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNav from './AdminNav';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const EmailSettingsPage = () => {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    transcription_request: null,
    transcription_completion: null,
    pm_assigning_to_assignee: null
  });
  const [, setLoading] = useState(true); // eslint-disable-line no-unused-vars -- setLoading used
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFrom, setSavingFrom] = useState(false);
  const [resendFrom, setResendFrom] = useState('');
  const [savingGoogleChatWebhook, setSavingGoogleChatWebhook] = useState(false);
  const [googleChatWebhookUrl, setGoogleChatWebhookUrl] = useState('');
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        const userRole = session.user.app_metadata?.role || session.user.role;
        
        if (userRole === 'admin') {
          setIsAdmin(true);
          await fetchTemplates();
          await fetchEmailSettings();
        } else {
          setIsAdmin(false);
          setError('Access denied. You do not have admin privileges.');
        }
      } catch (err) {
        console.error('Error checking admin role:', err);
        setError('Failed to verify permissions.');
      } finally {
        setCheckingRole(false);
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAdminRole();
    }
  }, [authLoading, navigate, setLoading, setCheckingRole]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/email-templates`);
      if (response.data?.templates) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load email templates.');
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/email-settings`);
      if (response.data?.settings) {
        setEmailSettings({
          transcription_request: response.data.settings.transcription_request || null,
          transcription_completion: response.data.settings.transcription_completion || null,
          pm_assigning_to_assignee: response.data.settings.pm_assigning_to_assignee || null
        });
      }
      if (response.data?.resend_from !== undefined) {
        setResendFrom(response.data.resend_from || '');
      }
      if (response.data?.google_chat_webhook_url !== undefined) {
        setGoogleChatWebhookUrl(response.data.google_chat_webhook_url || '');
      }
    } catch (err) {
      console.error('Error fetching email settings:', err);
      setError('Failed to load email settings.');
    }
  };

  const handleGoogleChatWebhookSave = async () => {
    try {
      setSavingGoogleChatWebhook(true);
      setMessage(null);
      setError(null);
      const response = await axios.post(`${API_BASE}/admin/email-settings/google-chat-webhook`, {
        google_chat_webhook_url: googleChatWebhookUrl || null
      });
      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'Google Chat webhook URL updated.' });
      }
    } catch (err) {
      console.error('Error updating Google Chat webhook URL:', err);
      setError(err.response?.data?.detail || 'Failed to update Google Chat webhook URL.');
    } finally {
      setSavingGoogleChatWebhook(false);
    }
  };

  const handleResendFromSave = async () => {
    try {
      setSavingFrom(true);
      setMessage(null);
      setError(null);
      const response = await axios.post(`${API_BASE}/admin/email-settings/from`, {
        resend_from: resendFrom || null
      });
      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'Resend from address updated.' });
      }
    } catch (err) {
      console.error('Error updating Resend from address:', err);
      setError(err.response?.data?.detail || 'Failed to update Resend from address.');
    } finally {
      setSavingFrom(false);
    }
  };

  const handleSyncTemplates = async () => {
    try {
      setSyncing(true);
      setMessage(null);
      setError(null);
      
      const response = await axios.post(`${API_BASE}/admin/email-templates/sync`);
      
      if (response.data?.success) {
        setMessage({
          type: 'success',
          text: response.data.message || `Successfully synced ${response.data.synced_count || 0} templates`
        });
        // Refresh templates after sync
        await fetchTemplates();
      }
    } catch (err) {
      console.error('Error syncing templates:', err);
      setError(err.response?.data?.detail || 'Failed to sync templates from Resend.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSettingChange = async (settingKey, templateId) => {
    try {
      setSaving(true);
      setMessage(null);
      setError(null);
      
      const response = await axios.post(`${API_BASE}/admin/email-settings`, {
        setting_key: settingKey,
        template_id: templateId || null
      });
      
      if (response.data?.success) {
        setEmailSettings(prev => ({
          ...prev,
          [settingKey]: templateId || null
        }));
        setMessage({
          type: 'success',
          text: response.data.message || 'Email setting updated successfully'
        });
      }
    } catch (err) {
      console.error('Error updating email setting:', err);
      setError(err.response?.data?.detail || 'Failed to update email setting.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold">Checking permissions...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
        <p className="text-gray-700 mb-6">{error || 'You must be an admin to view this page.'}</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-medium transition-colors"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-3xl font-medium text-gray-900">Email Settings</h1>
            <p className="text-sm text-gray-500 mt-2">
              Manage email templates and configure email notifications for transcription workflows.
            </p>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium whitespace-nowrap border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors"
          >
            ← Back to App
          </button>
        </div>

        <AdminNav />

        {/* Sync Templates Section */}
        <div className="mb-8 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Email Templates</h2>
            <button
              onClick={handleSyncTemplates}
              disabled={syncing}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {syncing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sync Templates from Resend
                </>
              )}
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Sync email templates from your Resend account. Templates will be stored in the database for selection.
          </p>
          {templates.length > 0 && (
            <div className="text-sm text-gray-500">
              {templates.length} template{templates.length !== 1 ? 's' : ''} available
            </div>
          )}
        </div>

        {/* Email Settings Section */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Email Configuration</h2>
          
          <div className="space-y-6">
            {/* Resend From Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resend From Address
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Sender email address used for Resend (e.g. notifications@yourdomain.com). Stored in admin DB; .env RESEND_FROM is used as fallback if empty.
              </p>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={resendFrom}
                  onChange={(e) => setResendFrom(e.target.value)}
                  placeholder="e.g. notifications@yourdomain.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleResendFromSave}
                  disabled={savingFrom}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {savingFrom ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Google Chat Webhook URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Chat Webhook URL
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Incoming webhook URL for Google Chat. Used to notify when a new transcription project is created. Stored in admin DB; .env GOOGLE_CHAT_WEBHOOK_URL is used as fallback if empty.
              </p>
              <div className="flex gap-2 items-center">
                <input
                  type="url"
                  value={googleChatWebhookUrl}
                  onChange={(e) => setGoogleChatWebhookUrl(e.target.value)}
                  placeholder="https://chat.googleapis.com/..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleGoogleChatWebhookSave}
                  disabled={savingGoogleChatWebhook}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {savingGoogleChatWebhook ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Transcription Request Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transcription Request Email
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select the email template to send when a transcription request is created.
              </p>
              <select
                value={emailSettings.transcription_request || ''}
                onChange={(e) => handleSettingChange('transcription_request', e.target.value || null)}
                disabled={saving || templates.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Template --</option>
                {templates.map((template) => (
                  <option key={template.template_id} value={template.template_id}>
                    {template.template_name || template.template_id}
                  </option>
                ))}
              </select>
            </div>

            {/* Transcription Completion Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transcription Completion Email
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select the email template to send when a transcription is completed.
              </p>
              <select
                value={emailSettings.transcription_completion || ''}
                onChange={(e) => handleSettingChange('transcription_completion', e.target.value || null)}
                disabled={saving || templates.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Template --</option>
                {templates.map((template) => (
                  <option key={template.template_id} value={template.template_id}>
                    {template.template_name || template.template_id}
                  </option>
                ))}
              </select>
            </div>

            {/* PM Assigning to Assignee Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PM Assigning to Assignee Email
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select the email template to send when a PM assigns a project to an assignee.
              </p>
              <select
                value={emailSettings.pm_assigning_to_assignee || ''}
                onChange={(e) => handleSettingChange('pm_assigning_to_assignee', e.target.value || null)}
                disabled={saving || templates.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Template --</option>
                {templates.map((template) => (
                  <option key={template.template_id} value={template.template_id}>
                    {template.template_name || template.template_id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Messages */}
          {message && (
            <div className={`mt-6 p-3 rounded-md ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {error && (
            <div className="mt-6 p-3 rounded-md bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Email templates are synced from Resend and stored in the database for quick access.</p>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsPage;
